import type { Timer } from "app/pages/sales-intelligence/types";
import { useRef } from "react";

type HookParams<P extends readonly unknown[]> = {
    delay: number;
    action(...params: P): void;
};

type HookValue<P extends readonly unknown[]> = {
    delayedAction(...params: P): void;
    cancelDelayedAction(): void;
};

const useControlledDelayedAction = <P extends readonly unknown[] = readonly unknown[]>({
    delay,
    action,
}: HookParams<P>): HookValue<P> => {
    const delayTimer = useRef<Timer | null>(null);

    const delayedAction = (...params: P): void => {
        delayTimer.current = setTimeout(() => action(...params), delay);
    };

    const cancelDelayedAction = (): void => {
        if (delayTimer.current === null) {
            return;
        }

        clearTimeout(delayTimer.current);
        delayTimer.current = null;
    };

    return { delayedAction, cancelDelayedAction };
};

export default useControlledDelayedAction;
